import {
  Banknote,
  CalendarDays,
  Landmark,
  PartyPopper,
  Plus,
  Repeat2,
  Trophy,
  X,
} from "lucide-react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "@/lib/goals";

const formatDate = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("pt-BR");
};

const sumByMethod = (items, method) =>
  items
    .filter((item) => item.method === method)
    .reduce((sum, item) => sum + item.value_cents, 0);

export const GoalCompletion = ({ goal, history, onClose, onRepeat }) => {
  const navigate = useNavigate();
  const pixTotal = sumByMethod(history, "pix");
  const cashTotal = sumByMethod(history, "cash");
  const pixCount = history.filter((item) => item.method === "pix").length;
  const cashCount = history.filter((item) => item.method === "cash").length;

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div
      data-testid="completion-screen-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="completion-screen-title"
      className="completion-backdrop"
    >
      <div className="completion-confetti" aria-hidden="true">
        {Array.from({ length: 24 }, (_, index) => <i key={index} style={{ "--piece": index }} />)}
      </div>
      <section className="completion-panel">
        <button
          type="button"
          onClick={onClose}
          data-testid="completion-close-button"
          className="absolute right-4 top-4 grid h-11 w-11 place-items-center rounded-full text-emerald-950 hover:bg-emerald-100"
          aria-label="Fechar conclusão"
        >
          <X className="h-6 w-6" />
        </button>
        <div className="completion-emblem" aria-hidden="true">
          <Trophy className="h-12 w-12" />
        </div>
        <p className="mt-5 flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-wide text-amber-800">
          <PartyPopper className="h-4 w-4" /> Conquista desbloqueada
        </p>
        <h2
          id="completion-screen-title"
          className="mt-2 font-heading text-3xl font-extrabold text-emerald-950 sm:text-4xl"
        >
          Parabéns! Meta concluída.
        </h2>
        <p className="mt-3 text-lg text-slate-700">
          Você concluiu “{goal.title}”. Seu esforço virou resultado.
        </p>
        <div className="mt-7 grid grid-cols-2 overflow-hidden rounded-2xl border border-amber-200 bg-amber-50/70 text-left">
          <div className="border-b border-r border-amber-200 p-4">
            <p className="text-sm font-semibold text-slate-600">Valor total</p>
            <p data-testid="completion-total-value" className="mt-1 text-xl font-extrabold text-emerald-950">
              {formatCurrency(goal.amount_cents)}
            </p>
          </div>
          <div className="border-b border-amber-200 p-4">
            <p className="text-sm font-semibold text-slate-600">Depósitos</p>
            <p data-testid="completion-deposit-count" className="mt-1 text-xl font-extrabold text-emerald-950">
              {history.length}
            </p>
          </div>
          <div className="border-r border-amber-200 p-4">
            <p className="flex items-center gap-1 text-sm font-semibold text-slate-600">
              <CalendarDays className="h-4 w-4" /> Início
            </p>
            <p data-testid="completion-start-date" className="mt-1 font-bold text-emerald-950">
              {formatDate(goal.created_at)}
            </p>
          </div>
          <div className="p-4">
            <p className="flex items-center gap-1 text-sm font-semibold text-slate-600">
              <CalendarDays className="h-4 w-4" /> Conclusão
            </p>
            <p data-testid="completion-finish-date" className="mt-1 font-bold text-emerald-950">
              {formatDate(goal.completed_at)}
            </p>
          </div>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <div data-testid="completion-pix-split" className="completion-split completion-split-pix">
            <Landmark className="h-6 w-6" />
            <span><span className="block text-sm font-semibold">Pix · {pixCount}</span><strong>{formatCurrency(pixTotal)}</strong></span>
          </div>
          <div data-testid="completion-cash-split" className="completion-split completion-split-cash">
            <Banknote className="h-6 w-6" />
            <span><span className="block text-sm font-semibold">Dinheiro · {cashCount}</span><strong>{formatCurrency(cashTotal)}</strong></span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate("/metas/nova")}
          data-testid="create-new-goal-button"
          className="mt-7 inline-flex min-h-[56px] w-full items-center justify-center gap-2 rounded-xl bg-emerald-700 px-5 font-heading text-xl font-bold text-white shadow-[0_5px_0_#064e3b] transition-[transform,box-shadow,background-color] hover:bg-emerald-800 active:translate-y-1 active:shadow-[0_1px_0_#064e3b]"
        >
          <Plus className="h-6 w-6" /> Criar nova meta
        </button>
        {onRepeat && !goal.repeat_scheduled_for && !goal.is_archived && (
          <button
            type="button"
            onClick={onRepeat}
            data-testid="repeat-next-month-button"
            className="mt-3 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl border-2 border-emerald-200 bg-white px-5 text-lg font-bold text-emerald-900 hover:bg-emerald-50"
          >
            <Repeat2 className="h-5 w-5" /> Repetir esta meta no próximo mês
          </button>
        )}
      </section>
    </div>
  );
};